import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import cocktailImage from "@/assets/cocktail-signature.jpg";

interface Drink {
  id: string;
  name: string;
  description: string | null;
  price: number;
  category: string;
  image_url: string | null;
}

const menuCategories = [
  { id: "signature", label: "Signature" },
  { id: "classic", label: "Classic" },
  { id: "spirits", label: "Spirits" },
  { id: "wine", label: "Wine" },
  { id: "beer", label: "Beer" },
];

export function DrinksMenu() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeCategory, setActiveCategory] = useState("signature");
  const [drinks, setDrinks] = useState<Record<string, Drink[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDrinks();
  }, []);

  const fetchDrinks = async () => {
    const { data, error } = await supabase
      .from('drinks')
      .select('*')
      .eq('is_available', true)
      .order('sort_order');

    if (!error && data) {
      const grouped = data.reduce((acc, drink) => {
        if (!acc[drink.category]) acc[drink.category] = [];
        acc[drink.category].push(drink);
        return acc;
      }, {} as Record<string, Drink[]>);
      setDrinks(grouped);
    }
    setLoading(false);
  };

  const currentDrinks = drinks[activeCategory] || [];

  return (
    <section id="drinks" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/30 to-background" />

      <div className="container mx-auto px-4 relative" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-primary font-medium tracking-[0.2em] text-sm mb-4">
            OUR SELECTION
          </p>
          <h2 className="section-heading text-foreground underline-gold">
            DRINKS MENU
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12"
        >
          {menuCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 ${
                activeCategory === category.id
                  ? "bg-primary text-primary-foreground shadow-[0_0_20px_hsla(43,74%,49%,0.4)]"
                  : "bg-secondary/50 text-foreground/70 hover:bg-secondary hover:text-foreground"
              }`}
            >
              {category.label}
            </button>
          ))}
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeCategory === "signature" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="md:col-span-2 lg:col-span-1 lg:row-span-2"
            >
              <div className="relative h-full min-h-[300px] rounded-lg overflow-hidden">
                <img src={cocktailImage} alt="Signature Cocktail" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-primary text-sm font-medium mb-2">FEATURED</p>
                  <h3 className="font-display text-3xl text-foreground">Signature Collection</h3>
                </div>
              </div>
            </motion.div>
          )}

          {loading ? (
            [1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-card/50 animate-pulse rounded-lg" />
            ))
          ) : (
            currentDrinks.map((drink, index) => (
              <motion.div
                key={drink.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="menu-card rounded-lg group overflow-hidden"
              >
                {drink.image_url && (
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={drink.image_url} 
                      alt={drink.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
                      {drink.name}
                    </h3>
                    <span className="text-primary font-display text-xl">฿{drink.price}</span>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{drink.description}</p>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}